import { useQueryClient } from '@tanstack/react-query';
import {
  agentSessionsQueryKey,
  createAgentSession,
  getAgentSession,
  ReportStreamUpdate,
  sendAMessageToAgent,
  stopAgentSession,
  streamAgentMessage,
} from 'api/agent';
import {
  compact,
  filter,
  find,
  findIndex,
  findLastIndex,
  first,
  forEach,
  fromPairs,
  isEqual,
  keys,
  map,
  omit,
  size,
  slice,
  toPairs,
  uniq,
} from 'es-toolkit/compat';
import {
  AgentSession,
  AgentStreamAction,
  AgentStreamProgress,
  ConversationMessage,
  CreateAgentSessionRequest,
  DEFAULT_AGENT_CONVERSATION_MODEL,
  DEFAULT_AGENT_CONVERSATION_PROVIDER,
  TokenTrace,
} from 'interfaces/agentSession';
import React from 'react';
import { useLocation } from 'react-router-dom';

import { useOpenStudyReportRoute } from 'components/Copilot/hooks/useOpenStudyReportRoute';
import { StudyReportRouteState } from 'components/Pages/StudyReport/studyReportRouteNavigation';
import { useEncodedFilters } from 'utils/useEncodedFilters';
import { ChatMessage } from '../CopilotConversation';
import { toChatMessages } from '../helpers';
import { resolvePendingAutoSubmit } from '../pendingAutoSubmit';
// Local temporary IDs are used for optimistic user/assistant messages before persistence.
let nextMsgId = 1;
const generateMsgId = () => `msg-${nextMsgId++}`;
const STREAM_RESUME_POLL_INTERVAL_MS = 1500;

// Streaming action updates re-use the same id; merge updates into existing action rows.
export const upsertAgentAction = (actions: AgentStreamAction[], nextAction: AgentStreamAction): AgentStreamAction[] => {
  const existingIndex = findIndex(actions, (action) => action.id === nextAction.id);
  if (existingIndex < 0) {
    return [...actions, nextAction];
  }

  return map(actions, (action, index) => (index === existingIndex ? { ...action, ...nextAction } : action));
};

type CopilotChatFlowState = {
  promptDraft: string;
};

type CopilotChatFlowSetters = {
  setPromptDraft: React.Dispatch<React.SetStateAction<string>>;
};

export type CopilotPromptSendState = 'idle' | 'submitting' | 'streaming';

interface PendingPromptSubmission {
  id: number;
  sessionId: string | null;
  awaitingSessionSelection: boolean;
}

export interface CopilotSessionActivityState {
  isStreaming: boolean;
  hasUnreadUpdate: boolean;
}

interface CopilotSessionViewState extends CopilotSessionActivityState {
  messages: ChatMessage[];
  agentStatus: string | null;
  agentProgress: AgentStreamProgress | null;
  agentActions: AgentStreamAction[];
  tokenTraces: TokenTrace[];
}

const createEmptySessionViewState = (): CopilotSessionViewState => ({
  messages: [],
  isStreaming: false,
  hasUnreadUpdate: false,
  agentStatus: null,
  agentProgress: null,
  agentActions: [],
  tokenTraces: [],
});

const buildConversationMessageFromChatMessage = ({
  message,
  previousMessage,
  isLatestAssistantMessage,
  viewState,
  timestamp,
}: {
  message: ChatMessage;
  previousMessage?: ConversationMessage;
  isLatestAssistantMessage: boolean;
  viewState: CopilotSessionViewState;
  timestamp: string;
}): ConversationMessage => {
  const nextStatus = message.isError ? 'failed' : message.isStreaming ? 'streaming' : 'completed';
  const shouldPersistLiveState =
    message.role === 'assistant' &&
    isLatestAssistantMessage &&
    (message.isStreaming ||
      Boolean(viewState.agentStatus) ||
      Boolean(viewState.agentProgress) ||
      size(viewState.agentActions) > 0);

  return {
    ...previousMessage,
    id: message.id,
    role: message.role,
    content: message.content,
    createdAt: previousMessage?.createdAt || timestamp,
    updatedAt: message.role === 'assistant' ? timestamp : previousMessage?.updatedAt,
    status: nextStatus,
    tokenTrace: message.tokenTrace ?? previousMessage?.tokenTrace ?? null,
    followUpOptions: message.followUpOptions || previousMessage?.followUpOptions || [],
    liveState: shouldPersistLiveState
      ? {
          statusText: viewState.agentStatus || null,
          progress: viewState.agentProgress || null,
          actions: viewState.agentActions,
          startedAt: previousMessage?.liveState?.startedAt || previousMessage?.createdAt || timestamp,
          updatedAt: timestamp,
          ...(message.isStreaming ? {} : { completedAt: timestamp }),
        }
      : null,
  };
};

const applySessionViewStateToSession = (session: AgentSession, viewState: CopilotSessionViewState): AgentSession => {
  const timestamp = new Date().toISOString();
  const previousMessagesById = fromPairs(
    map(session.sessionData.conversation.messages, (message) => [message.id, message])
  ) as Record<string, ConversationMessage>;
  const latestAssistantMessageIndex = findLastIndex(viewState.messages, (message) => message.role === 'assistant');
  const latestAssistantMessageId =
    latestAssistantMessageIndex >= 0 ? viewState.messages[latestAssistantMessageIndex].id : null;
  const nextChatMessagesById = fromPairs(
    map(viewState.messages, (message) => [
      message.id,
      buildConversationMessageFromChatMessage({
        message,
        previousMessage: previousMessagesById[message.id],
        isLatestAssistantMessage: message.id === latestAssistantMessageId,
        viewState,
        timestamp,
      }),
    ])
  ) as Record<string, ConversationMessage>;

  const nextConversationMessages = [
    ...compact(
      map(session.sessionData.conversation.messages, (previousMessage) => {
        if (previousMessage.role !== 'user' && previousMessage.role !== 'assistant') {
          return previousMessage;
        }

        return nextChatMessagesById[previousMessage.id] || null;
      })
    ),
    ...compact(
      map(viewState.messages, (message) =>
        previousMessagesById[message.id] ? null : nextChatMessagesById[message.id] || null
      )
    ),
  ];

  return {
    ...session,
    updatedAt: timestamp,
    sessionData: {
      ...session.sessionData,
      conversation: {
        ...session.sessionData.conversation,
        messages: nextConversationMessages,
        lastMessageAt: timestamp,
      },
    },
  };
};

const getCopilotPromptSendState = ({
  activeSessionId,
  selectedSessionIsStreaming,
  pendingPromptSubmission,
}: {
  activeSessionId: string | null;
  selectedSessionIsStreaming: boolean;
  pendingPromptSubmission: PendingPromptSubmission | null;
}): CopilotPromptSendState => {
  if (selectedSessionIsStreaming) {
    return 'streaming';
  }

  if (!pendingPromptSubmission) {
    return 'idle';
  }

  if (!pendingPromptSubmission.sessionId) {
    return 'submitting';
  }

  if (pendingPromptSubmission.awaitingSessionSelection) {
    if (!activeSessionId || activeSessionId === pendingPromptSubmission.sessionId) {
      return 'submitting';
    }

    return 'idle';
  }

  return pendingPromptSubmission.sessionId === activeSessionId ? 'submitting' : 'idle';
};

const getConversationMessages = (session?: AgentSession | null): ChatMessage[] =>
  session ? toChatMessages(session.sessionData.conversation.messages) : [];

const createSessionViewStateFromSession = (session?: AgentSession | null): CopilotSessionViewState => {
  const messages = getConversationMessages(session);

  if (!session) {
    return {
      ...createEmptySessionViewState(),
      messages,
    };
  }

  const conversationMessages = session.sessionData.conversation.messages;
  let latestAssistantMessage = null;

  for (let index = conversationMessages.length - 1; index >= 0; index -= 1) {
    const message = conversationMessages[index];

    if (message.role === 'assistant') {
      latestAssistantMessage = message;
      break;
    }
  }

  const liveState = latestAssistantMessage?.liveState;
  const isStreaming = latestAssistantMessage?.status === 'streaming';

  // Hydrate token traces from persisted messages so they survive page reload.
  const persistedTraces: TokenTrace[] = [];
  forEach(conversationMessages, (msg) => {
    if (msg.role === 'assistant' && msg.tokenTrace) {
      persistedTraces.push(msg.tokenTrace as TokenTrace);
    }
  });

  return {
    messages,
    isStreaming,
    hasUnreadUpdate: false,
    agentStatus: isStreaming ? liveState?.statusText || null : null,
    agentProgress: isStreaming ? liveState?.progress || null : null,
    agentActions: isStreaming ? liveState?.actions || [] : [],
    tokenTraces: persistedTraces,
  };
};

type CopilotChatFlowSessionContext = {
  activeSessionId: string | null;
  activeSession: AgentSession | undefined;
  activeSessionIsTransitionLoading: boolean;
  copilotDrawerOpen: boolean;
  selectedStudyIds: string[];
  selectedStudyId: string | null | undefined;
  labId: string | null;
  knownSessions: AgentSession[];
  currentUserId?: string;
  setFallbackSessionId: React.Dispatch<React.SetStateAction<string | null>>;
};

type UseCopilotChatFlowParams = {
  state: CopilotChatFlowState;
  setters: CopilotChatFlowSetters;
  sessionContext: CopilotChatFlowSessionContext;
};

// Owns chat lifecycle: hydrate prompt/messages, send/stream/fallback, retry, and stop.
export const useCopilotChatFlow = ({ state, setters, sessionContext }: UseCopilotChatFlowParams) => {
  const { promptDraft } = state;
  const { setPromptDraft } = setters;
  const {
    activeSessionId,
    activeSession,
    activeSessionIsTransitionLoading,
    copilotDrawerOpen,
    selectedStudyIds,
    selectedStudyId,
    labId,
    knownSessions,
    currentUserId,
    setFallbackSessionId,
  } = sessionContext;
  const location = useLocation();
  const queryClient = useQueryClient();
  const openStudyReportRoute = useOpenStudyReportRoute();
  const { queryParams } = useEncodedFilters();
  const locationPathname = location.pathname;
  const locationSearch = location.search;
  const locationState = location.state;
  const currentRouteState = locationState as StudyReportRouteState | null;
  const resultsMode = queryParams.resultsMode;

  const [sessionViewStateById, setSessionViewStateById] = React.useState<Record<string, CopilotSessionViewState>>({});
  const sessionViewStateByIdRef = React.useRef<Record<string, CopilotSessionViewState>>({});
  const abortControllersRef = React.useRef<Record<string, AbortController>>({});
  const optimisticMessageCountsRef = React.useRef<Record<string, number>>({});
  const restorePromptOnAbortRef = React.useRef<Record<string, boolean>>({});
  const locallyDismissedSessionsRef = React.useRef<Set<string>>(new Set());
  const activeSessionIdRef = React.useRef<string | null>(activeSessionId);
  const handleSendPromptRef = React.useRef<() => Promise<void>>(async () => {});
  const pendingAutoSubmitPromptRef = React.useRef<string | null>(null);
  const consumedPromptRef = React.useRef<string | null>(null);
  const ensureSessionPromiseRef = React.useRef<Promise<AgentSession> | null>(null);
  const nextPromptSubmissionIdRef = React.useRef(1);
  const pendingPromptSubmissionRef = React.useRef<PendingPromptSubmission | null>(null);

  const [pendingPromptSubmission, setPendingPromptSubmission] = React.useState<PendingPromptSubmission | null>(null);

  const persistedSelectedSessionViewState = React.useMemo(
    () => createSessionViewStateFromSession(activeSession),
    [activeSession]
  );

  const selectedSessionViewState = React.useMemo(
    () =>
      activeSessionId && sessionViewStateById[activeSessionId]
        ? sessionViewStateById[activeSessionId]
        : persistedSelectedSessionViewState,
    [activeSessionId, persistedSelectedSessionViewState, sessionViewStateById]
  );

  const selectedSessionMessages = selectedSessionViewState.messages;

  const setPendingPromptSubmissionState = React.useCallback((nextSubmission: PendingPromptSubmission | null) => {
    pendingPromptSubmissionRef.current = nextSubmission;
    setPendingPromptSubmission(nextSubmission);
  }, []);

  const replacePendingPromptSubmission = React.useCallback(
    (submissionId: number | null, nextSubmission: Omit<PendingPromptSubmission, 'id'>) => {
      if (!submissionId || pendingPromptSubmissionRef.current?.id !== submissionId) {
        return;
      }

      setPendingPromptSubmissionState({
        id: submissionId,
        ...nextSubmission,
      });
    },
    [setPendingPromptSubmissionState]
  );

  const clearPendingPromptSubmission = React.useCallback(
    (submissionId: number | null) => {
      if (!submissionId || pendingPromptSubmissionRef.current?.id !== submissionId) {
        return;
      }

      setPendingPromptSubmissionState(null);
    },
    [setPendingPromptSubmissionState]
  );

  const promptSendState = React.useMemo(
    () =>
      getCopilotPromptSendState({
        activeSessionId,
        selectedSessionIsStreaming: selectedSessionViewState.isStreaming,
        pendingPromptSubmission,
      }),
    [activeSessionId, pendingPromptSubmission, selectedSessionViewState.isStreaming]
  );

  const sessionActivityById = React.useMemo(
    () =>
      fromPairs(
        map(toPairs(sessionViewStateById), ([sessionId, sessionViewState]) => [
          sessionId,
          {
            isStreaming: sessionViewState.isStreaming,
            hasUnreadUpdate: sessionViewState.hasUnreadUpdate,
          },
        ])
      ) as Record<string, CopilotSessionActivityState>,
    [sessionViewStateById]
  );

  const backgroundSessionActivityCount = React.useMemo(
    () =>
      size(
        filter(
          toPairs(sessionActivityById),
          ([sessionId, sessionActivity]) =>
            sessionId !== activeSessionId && (sessionActivity.isStreaming || sessionActivity.hasUnreadUpdate)
        )
      ),
    [activeSessionId, sessionActivityById]
  );

  const hiddenSessionActivityCount = React.useMemo(() => {
    if (copilotDrawerOpen) {
      return 0;
    }

    const localActivityCount = size(
      filter(sessionActivityById, (sessionActivity) => sessionActivity.isStreaming || sessionActivity.hasUnreadUpdate)
    );
    const activeSessionActivity = activeSessionId ? sessionActivityById[activeSessionId] : null;
    const activeSessionAlreadyCounted = Boolean(
      activeSessionActivity && (activeSessionActivity.isStreaming || activeSessionActivity.hasUnreadUpdate)
    );

    return localActivityCount + (selectedSessionViewState.isStreaming && !activeSessionAlreadyCounted ? 1 : 0);
  }, [activeSessionId, copilotDrawerOpen, selectedSessionViewState.isStreaming, sessionActivityById]);

  React.useEffect(() => {
    sessionViewStateByIdRef.current = sessionViewStateById;
  }, [sessionViewStateById]);

  const updateSessionViewState = React.useCallback(
    (
      sessionId: string | null | undefined,
      updater: (currentState: CopilotSessionViewState) => CopilotSessionViewState,
      { syncCache = false }: { syncCache?: boolean } = {}
    ) => {
      if (!sessionId) {
        return;
      }

      const currentState = sessionViewStateByIdRef.current[sessionId] || createEmptySessionViewState();
      const nextState = updater(currentState);

      if (isEqual(currentState, nextState)) {
        return;
      }

      sessionViewStateByIdRef.current = {
        ...sessionViewStateByIdRef.current,
        [sessionId]: nextState,
      };

      const cachedSession =
        queryClient.getQueryData<AgentSession>(['agentSession', sessionId]) ||
        find(knownSessions, (session) => session.id === sessionId) ||
        null;

      if (syncCache && cachedSession) {
        const nextSession = applySessionViewStateToSession(cachedSession, nextState);
        queryClient.setQueryData(['agentSession', sessionId], nextSession);
        queryClient.setQueryData(agentSessionsQueryKey, (sessions: AgentSession[] | undefined) => {
          if (!sessions) {
            return sessions;
          }

          let didUpdate = false;
          const nextSessions = map(sessions, (existingSession) => {
            if (existingSession.id !== sessionId) {
              return existingSession;
            }

            didUpdate = true;
            return nextSession;
          });

          return didUpdate ? nextSessions : sessions;
        });
      }

      setSessionViewStateById((currentStateById) => {
        if (isEqual(currentStateById[sessionId], nextState)) {
          return currentStateById;
        }

        return {
          ...currentStateById,
          [sessionId]: nextState,
        };
      });
    },
    [knownSessions, queryClient]
  );

  const clearSessionViewState = React.useCallback((sessionId: string | null | undefined) => {
    if (!sessionId) {
      return;
    }

    delete optimisticMessageCountsRef.current[sessionId];
    delete restorePromptOnAbortRef.current[sessionId];
    delete abortControllersRef.current[sessionId];
    locallyDismissedSessionsRef.current.delete(sessionId);
    if (sessionViewStateByIdRef.current[sessionId]) {
      const nextStateById = { ...sessionViewStateByIdRef.current };
      delete nextStateById[sessionId];
      sessionViewStateByIdRef.current = nextStateById;
    }

    setSessionViewStateById((currentStateById) => {
      if (!currentStateById[sessionId]) {
        return currentStateById;
      }

      const nextStateById = { ...currentStateById };
      delete nextStateById[sessionId];
      return nextStateById;
    });
  }, []);

  const markSessionSeen = React.useCallback((sessionId: string | null | undefined) => {
    if (!sessionId) {
      return;
    }

    const currentState = sessionViewStateByIdRef.current[sessionId];
    if (!currentState || !currentState.hasUnreadUpdate) {
      return;
    }

    sessionViewStateByIdRef.current = {
      ...sessionViewStateByIdRef.current,
      [sessionId]: {
        ...currentState,
        hasUnreadUpdate: false,
      },
    };

    setSessionViewStateById((currentStateById) => {
      const currentSessionState = currentStateById[sessionId];
      if (!currentSessionState || !currentSessionState.hasUnreadUpdate) {
        return currentStateById;
      }

      return {
        ...currentStateById,
        [sessionId]: {
          ...currentSessionState,
          hasUnreadUpdate: false,
        },
      };
    });
  }, []);

  const syncSessionCaches = React.useCallback(
    (session: AgentSession) => {
      queryClient.setQueryData(['agentSession', session.id], session);
      queryClient.setQueryData(agentSessionsQueryKey, (sessions: AgentSession[] | undefined) => {
        if (!sessions) {
          return sessions;
        }

        let didUpdate = false;
        const nextSessions = map(sessions, (existingSession) => {
          if (existingSession.id !== session.id) {
            return existingSession;
          }

          didUpdate = true;
          return session;
        });

        return didUpdate ? nextSessions : sessions;
      });
    },
    [queryClient]
  );

  const getCachedSessionById = React.useCallback(
    (sessionId: string) => {
      const cachedSession = queryClient.getQueryData<AgentSession>(['agentSession', sessionId]);

      if (cachedSession) {
        return cachedSession;
      }

      return find(knownSessions, (session) => session.id === sessionId) || null;
    },
    [knownSessions, queryClient]
  );

  const didPersistedSessionChange = React.useCallback(
    (previousSession: AgentSession | null, nextSession: AgentSession) => {
      if (!previousSession) {
        return false;
      }

      return !isEqual(
        createSessionViewStateFromSession(previousSession),
        createSessionViewStateFromSession(nextSession)
      );
    },
    []
  );

  const applyReloadedSessionData = React.useCallback(
    (session: AgentSession, { markUnread = false }: { markUnread?: boolean } = {}) => {
      syncSessionCaches(session);
      updateSessionViewState(session.id, (currentState) => ({
        ...createSessionViewStateFromSession(session),
        hasUnreadUpdate: session.id === activeSessionIdRef.current ? false : currentState.hasUnreadUpdate || markUnread,
      }));
    },
    [syncSessionCaches, updateSessionViewState]
  );

  const reloadPersistedSessionData = React.useCallback(
    async (sessionId: string, fallbackSession?: AgentSession) => {
      try {
        const refreshedSession = await getAgentSession(sessionId);
        applyReloadedSessionData(refreshedSession);
      } catch {
        if (fallbackSession) {
          applyReloadedSessionData(fallbackSession);
        }
      }
    },
    [applyReloadedSessionData]
  );

  const disconnectedStreamingSessionIds = React.useMemo(
    () =>
      filter(
        uniq(
          compact([
            activeSessionId && selectedSessionViewState.isStreaming ? activeSessionId : null,
            ...map(
              filter(toPairs(sessionViewStateById), ([, sessionViewState]) => sessionViewState.isStreaming),
              ([sessionId]) => sessionId
            ),
            ...map(
              filter(knownSessions, (session) => createSessionViewStateFromSession(session).isStreaming),
              'id'
            ),
          ])
        ),
        (sessionId) => !abortControllersRef.current[sessionId] && !locallyDismissedSessionsRef.current.has(sessionId)
      ),
    [activeSessionId, knownSessions, selectedSessionViewState.isStreaming, sessionViewStateById]
  );

  const stopStreamingSession = React.useCallback(
    (sessionId: string | null | undefined, { restorePrompt = false }: { restorePrompt?: boolean } = {}) => {
      if (!sessionId) {
        return;
      }

      const controller = abortControllersRef.current[sessionId];
      if (controller) {
        restorePromptOnAbortRef.current[sessionId] = restorePrompt;
        controller.abort();
        return;
      }

      // Disconnected session (no local abort controller) — clear streaming UI state
      // so the user isn't stuck with an unresponsive streaming indicator,
      // and tell the server to cancel the streaming turn.
      locallyDismissedSessionsRef.current.add(sessionId);
      updateSessionViewState(sessionId, (currentState) => {
        if (!currentState.isStreaming) {
          return currentState;
        }

        const streamingIndex = findLastIndex(currentState.messages, (msg) => Boolean(msg.isStreaming));
        let nextMessages = currentState.messages;
        if (streamingIndex >= 0 && !currentState.messages[streamingIndex].content) {
          const userIndex = streamingIndex - 1;
          const shouldRemoveUser = userIndex >= 0 && currentState.messages[userIndex]?.role === 'user';
          nextMessages = shouldRemoveUser
            ? [...slice(currentState.messages, 0, userIndex), ...slice(currentState.messages, streamingIndex + 1)]
            : [...slice(currentState.messages, 0, streamingIndex), ...slice(currentState.messages, streamingIndex + 1)];
        } else {
          nextMessages = map(currentState.messages, (msg) => (msg.isStreaming ? { ...msg, isStreaming: false } : msg));
        }

        return {
          ...currentState,
          messages: nextMessages,
          isStreaming: false,
          agentStatus: null,
          agentProgress: null,
          agentActions: [],
        };
      });

      // Fire-and-forget: mark the streaming turn as cancelled on the server,
      // then best-effort reload the persisted session payload so a disconnected
      // drawer rehydrates the conversation instead of staying on local fallback state.
      stopAgentSession(sessionId)
        .then((stoppedSession) => {
          applyReloadedSessionData(stoppedSession);
          void reloadPersistedSessionData(sessionId, stoppedSession);
        })
        .catch(() => {
          void reloadPersistedSessionData(sessionId);
        });
    },
    [applyReloadedSessionData, reloadPersistedSessionData, updateSessionViewState]
  );

  const stopAllStreamingSessions = React.useCallback(() => {
    forEach(keys(abortControllersRef.current), (sessionId) => {
      restorePromptOnAbortRef.current[sessionId] = false;
      abortControllersRef.current[sessionId]?.abort();
    });
  }, []);

  const getBaseMessagesForSession = React.useCallback(
    (sessionId: string, session?: AgentSession | null) => {
      const localSessionViewState = sessionViewStateByIdRef.current[sessionId];
      if (localSessionViewState) {
        return localSessionViewState.messages;
      }

      const cachedSession = session || queryClient.getQueryData<AgentSession>(['agentSession', sessionId]);
      return createSessionViewStateFromSession(cachedSession).messages;
    },
    [queryClient]
  );

  const isChatLoading = Boolean(
    activeSessionId && activeSessionIsTransitionLoading && !selectedSessionViewState.isStreaming
  );

  React.useEffect(() => {
    const currentPendingSubmission = pendingPromptSubmission;

    if (!currentPendingSubmission?.sessionId) {
      return;
    }

    if (!sessionViewStateById[currentPendingSubmission.sessionId]?.isStreaming) {
      return;
    }

    if (currentPendingSubmission.awaitingSessionSelection && activeSessionId !== currentPendingSubmission.sessionId) {
      return;
    }

    clearPendingPromptSubmission(currentPendingSubmission.id);
  }, [activeSessionId, clearPendingPromptSubmission, pendingPromptSubmission, sessionViewStateById]);

  React.useEffect(() => {
    activeSessionIdRef.current = activeSessionId;
    if (activeSessionId) {
      markSessionSeen(activeSessionId);
    }
  }, [activeSessionId, markSessionSeen]);

  React.useEffect(() => {
    // Pull one-time prompt injection from route state (used by "open in copilot" flows).
    const incomingRouteState = locationState as StudyReportRouteState | null;
    const routedPrompt = incomingRouteState?.draftConversation?.initialUserPrompt;
    const autoSubmit = incomingRouteState?.draftConversation?.autoSubmit;

    if (routedPrompt && routedPrompt !== consumedPromptRef.current) {
      consumedPromptRef.current = routedPrompt;
      setPromptDraft(routedPrompt);
      if (autoSubmit) {
        pendingAutoSubmitPromptRef.current = routedPrompt.trim() || null;
      }

      const restState = omit(incomingRouteState as StudyReportRouteState, ['draftConversation']);
      window.history.replaceState({ ...restState }, '');
    }
  }, [locationState, setPromptDraft]);

  React.useEffect(() => {
    if (activeSession) {
      const persistedSessionViewState = createSessionViewStateFromSession(activeSession);
      const nextMessages = persistedSessionViewState.messages;
      const optimisticMessageCount = optimisticMessageCountsRef.current[activeSession.id] || 0;
      const ownsLocalStream = Boolean(abortControllersRef.current[activeSession.id]);

      if (!selectedSessionViewState.isStreaming && size(nextMessages) >= optimisticMessageCount) {
        delete optimisticMessageCountsRef.current[activeSession.id];
      }

      updateSessionViewState(activeSession.id, (currentState) => {
        if (currentState.isStreaming && ownsLocalStream) {
          return currentState;
        }

        if (locallyDismissedSessionsRef.current.has(activeSession.id)) {
          return currentState;
        }

        if (currentState.isStreaming && !ownsLocalStream && size(nextMessages) < size(currentState.messages)) {
          return currentState;
        }

        if (size(nextMessages) < optimisticMessageCount) {
          return currentState;
        }

        return {
          ...persistedSessionViewState,
          hasUnreadUpdate: activeSession.id === activeSessionIdRef.current ? false : currentState.hasUnreadUpdate,
        };
      });
      setFallbackSessionId(activeSession.id);
    }
  }, [activeSession, selectedSessionViewState.isStreaming, setFallbackSessionId, updateSessionViewState]);

  React.useEffect(() => {
    if (size(disconnectedStreamingSessionIds) === 0) {
      return undefined;
    }

    let isDisposed = false;
    let isPolling = false;

    const pollPersistedStreamingSessions = async () => {
      if (isDisposed || isPolling) {
        return;
      }

      isPolling = true;
      try {
        const refreshedSessions = await Promise.all(
          map(disconnectedStreamingSessionIds, async (sessionId) => {
            try {
              const previousSession = getCachedSessionById(sessionId);
              const refreshedSession = await getAgentSession(sessionId);

              return { previousSession, refreshedSession };
            } catch {
              return null;
            }
          })
        );

        if (!isDisposed) {
          map(refreshedSessions, (sessionResult) => {
            if (!sessionResult) {
              return null;
            }

            const { previousSession, refreshedSession } = sessionResult;
            applyReloadedSessionData(refreshedSession, {
              markUnread:
                refreshedSession.id !== activeSessionIdRef.current &&
                didPersistedSessionChange(previousSession, refreshedSession),
            });

            return null;
          });
        }
      } catch {
        // Resume polling is best-effort. Keep showing the last persisted state
        // instead of surfacing transient reconnect noise in the drawer.
      } finally {
        isPolling = false;
      }
    };

    void pollPersistedStreamingSessions();
    const pollIntervalId = window.setInterval(pollPersistedStreamingSessions, STREAM_RESUME_POLL_INTERVAL_MS);

    return () => {
      isDisposed = true;
      window.clearInterval(pollIntervalId);
    };
  }, [applyReloadedSessionData, didPersistedSessionChange, disconnectedStreamingSessionIds, getCachedSessionById]);

  // Session payload keeps report context + navigation breadcrumbs for backend/audit continuity.
  const buildCreateSessionRequest = (): CreateAgentSessionRequest => {
    const routeStudyIds = currentRouteState?.reportContext.selectedStudyIds || [];
    const normalizedStudyIds = uniq(compact([...selectedStudyIds, ...routeStudyIds]));
    const activeStudyId = normalizedStudyIds[0] || null;
    const normalizedLabId = labId || currentRouteState?.reportContext.labId || null;
    const userId = currentUserId || 'unknown';

    return {
      studyIds: normalizedStudyIds,
      sessionData: {
        schemaVersion: 1,
        reportState: {
          status: 'draft',
          studyIds: normalizedStudyIds,
          sections: [],
          ...(activeStudyId ? { activeStudyId } : {}),
          ...(normalizedLabId || size(normalizedStudyIds)
            ? {
                filters: {
                  ...(normalizedLabId ? { labId: normalizedLabId } : {}),
                  studyIds: normalizedStudyIds,
                },
              }
            : {}),
        },
        conversation: {
          provider: DEFAULT_AGENT_CONVERSATION_PROVIDER,
          model: DEFAULT_AGENT_CONVERSATION_MODEL,
          messages: [],
        },
        navigation: {
          openedFrom: { pathname: locationPathname, search: locationSearch },
          returnToOnClose: { pathname: locationPathname, search: locationSearch },
        },
        audit: {
          createdByUserId: userId,
          updatedByUserId: userId,
        },
      },
    };
  };

  const ensureSession = async (): Promise<AgentSession> => {
    if (activeSession) {
      return activeSession;
    }

    if (activeSessionId) {
      const loadedSession = await getAgentSession(activeSessionId);
      queryClient.setQueryData(['agentSession', activeSessionId], loadedSession);
      return loadedSession;
    }

    // Dedup concurrent session creation — second caller joins the first's in-flight promise.
    if (ensureSessionPromiseRef.current) {
      return ensureSessionPromiseRef.current;
    }

    const createPromise = (async () => {
      const created = await createAgentSession(buildCreateSessionRequest());
      setFallbackSessionId(created.id);
      await queryClient.cancelQueries(agentSessionsQueryKey);
      queryClient.setQueryData(['agentSession', created.id], created);
      queryClient.setQueryData(['sessionDatasets', created.id], []);
      queryClient.setQueryData(agentSessionsQueryKey, (sessions: AgentSession[] | undefined) => {
        const nextSessions = sessions || [];

        return [created, ...filter(nextSessions, (session) => session.id !== created.id)];
      });
      return created;
    })();

    ensureSessionPromiseRef.current = createPromise;
    try {
      return await createPromise;
    } finally {
      ensureSessionPromiseRef.current = null;
    }
  };

  const handleSendPrompt = async () => {
    const text = promptDraft.trim();
    if (!text) return;

    if (
      getCopilotPromptSendState({
        activeSessionId,
        selectedSessionIsStreaming: selectedSessionViewState.isStreaming,
        pendingPromptSubmission: pendingPromptSubmissionRef.current,
      }) !== 'idle'
    ) {
      return;
    }

    pendingAutoSubmitPromptRef.current = null;
    let targetSessionId: string | null = null;
    let userMessageId: string | null = null;
    let assistantMessageId: string | null = null;
    const initialSessionId = activeSessionId;
    const promptSubmissionId = nextPromptSubmissionIdRef.current++;

    setPendingPromptSubmissionState({
      id: promptSubmissionId,
      sessionId: initialSessionId,
      awaitingSessionSelection: false,
    });

    try {
      const session = await ensureSession();
      targetSessionId = session.id;
      locallyDismissedSessionsRef.current.delete(session.id);
      replacePendingPromptSubmission(promptSubmissionId, {
        sessionId: session.id,
        awaitingSessionSelection: !initialSessionId && activeSessionIdRef.current !== session.id,
      });

      const baseMessages = getBaseMessagesForSession(session.id, session);
      const userMsg: ChatMessage = { id: generateMsgId(), role: 'user', content: text };
      const assistantMsgId = generateMsgId();
      const assistantMsg: ChatMessage = {
        id: assistantMsgId,
        role: 'assistant',
        content: '',
        isStreaming: true,
        tokenTrace: null,
        followUpOptions: [],
      };
      const optimisticMessageCount = size(baseMessages) + 2;
      userMessageId = userMsg.id;
      assistantMessageId = assistantMsg.id;

      if (sessionViewStateByIdRef.current[session.id]?.isStreaming) {
        clearPendingPromptSubmission(promptSubmissionId);
        return;
      }

      setPromptDraft('');
      optimisticMessageCountsRef.current[session.id] = optimisticMessageCount;

      // Set the abort controller BEFORE the optimistic state update so the
      // sync effect's `ownsLocalStream` guard sees it and preserves the
      // streaming state instead of overwriting it with persisted (empty) data.
      const controller = new AbortController();
      abortControllersRef.current[session.id] = controller;
      restorePromptOnAbortRef.current[session.id] = false;

      updateSessionViewState(
        session.id,
        (currentState) => ({
          ...currentState,
          messages: [...baseMessages, userMsg, assistantMsg],
          isStreaming: true,
          hasUnreadUpdate: false,
          agentStatus: null,
          agentProgress: null,
          agentActions: [],
        }),
        { syncCache: true }
      );

      try {
        // Prefer stream endpoint for progressive rendering and agent status updates.
        await streamAgentMessage(
          {
            user_request: text,
            session_id: session.id,
            origin: window.location.origin,
            results_mode: resultsMode,
          },
          (fullText) => {
            updateSessionViewState(
              session.id,
              (currentState) => ({
                ...currentState,
                messages: map(currentState.messages, (message) =>
                  message.id === assistantMessageId ? { ...message, content: fullText } : message
                ),
                hasUnreadUpdate:
                  currentState.hasUnreadUpdate || (Boolean(fullText) && activeSessionIdRef.current !== session.id),
              }),
              { syncCache: true }
            );
          },
          controller.signal,
          (statusText) => {
            updateSessionViewState(
              session.id,
              (currentState) => ({
                ...currentState,
                agentStatus: statusText,
              }),
              { syncCache: true }
            );
          },
          (progressEvent) => {
            updateSessionViewState(
              session.id,
              (currentState) => ({
                ...currentState,
                agentProgress: progressEvent,
              }),
              { syncCache: true }
            );
          },
          (actionEvent) => {
            updateSessionViewState(
              session.id,
              (currentState) => ({
                ...currentState,
                agentActions: upsertAgentAction(currentState.agentActions, actionEvent),
              }),
              { syncCache: true }
            );
          },
          (trace) => {
            updateSessionViewState(
              session.id,
              (currentState) => ({
                ...currentState,
                messages: map(currentState.messages, (message) =>
                  message.id === assistantMessageId ? { ...message, tokenTrace: trace } : message
                ),
                tokenTraces: [...currentState.tokenTraces, trace],
              }),
              { syncCache: true }
            );
          },
          (followUpOptions) => {
            updateSessionViewState(
              session.id,
              (currentState) => ({
                ...currentState,
                messages: map(currentState.messages, (message) =>
                  message.id === assistantMessageId ? { ...message, followUpOptions: followUpOptions || [] } : message
                ),
              }),
              { syncCache: true }
            );
          },
          (update: ReportStreamUpdate) => {
            // A report-mutation tool completed. If the event carries a full
            // session snapshot, apply it directly to the query cache for an
            // instant (zero-latency) report canvas update.
            if (update.session) {
              queryClient.setQueryData(['agentSession', session.id], update.session);
            } else {
              // Fallback: invalidate so React Query refetches.
              void queryClient.invalidateQueries(['agentSession', session.id]);
            }

            // When a chart tool completes with plotly data, attach the chart
            // preview to the most recent matching action so the timeline can
            // render a live mini-chart.
            if (update.chart) {
              const chartPreview = update.chart;
              updateSessionViewState(
                session.id,
                (currentState) => {
                  // Find the last completed chart action and enrich it.
                  const enrichedActions = [...currentState.agentActions];
                  for (let i = enrichedActions.length - 1; i >= 0; i--) {
                    const action = enrichedActions[i];
                    if (action.name.includes('chart') && action.status === 'completed' && !action.chartPreview) {
                      enrichedActions[i] = { ...action, chartPreview };
                      break;
                    }
                  }
                  return { ...currentState, agentActions: enrichedActions };
                },
                { syncCache: true }
              );
            }

            // When a table tool completes with table data, attach the table
            // preview to the most recent matching action.
            if (update.table) {
              const tablePreview = update.table;
              updateSessionViewState(
                session.id,
                (currentState) => {
                  const enrichedActions = [...currentState.agentActions];
                  for (let i = enrichedActions.length - 1; i >= 0; i--) {
                    const action = enrichedActions[i];
                    if (action.name.includes('table') && action.status === 'completed' && !action.tablePreview) {
                      enrichedActions[i] = { ...action, tablePreview };
                      break;
                    }
                  }
                  return { ...currentState, agentActions: enrichedActions };
                },
                { syncCache: true }
              );
            }
          }
        );
      } catch (streamError: unknown) {
        // Backward-compatible fallback for deployments without stream endpoint.
        const isEndpointMissing =
          streamError instanceof Error && /status (404|405)|Method Not Allowed|Not Found/i.test(streamError.message);
        if (!isEndpointMissing) throw streamError;

        const response = await sendAMessageToAgent({
          user_request: text,
          session_id: session.id,
          origin: window.location.origin,
          results_mode: resultsMode,
        });
        updateSessionViewState(
          session.id,
          (currentState) => ({
            ...currentState,
            messages: map(currentState.messages, (message) =>
              message.id === assistantMessageId
                ? {
                    ...message,
                    content: response.message,
                    followUpOptions: response.followUpOptions || [],
                  }
                : message
            ),
            hasUnreadUpdate: currentState.hasUnreadUpdate || activeSessionIdRef.current !== session.id,
          }),
          { syncCache: true }
        );
      }

      updateSessionViewState(
        session.id,
        (currentState) => ({
          ...currentState,
          messages: map(currentState.messages, (message) =>
            message.id === assistantMessageId ? { ...message, isStreaming: false } : message
          ),
          isStreaming: false,
          hasUnreadUpdate: currentState.hasUnreadUpdate || activeSessionIdRef.current !== session.id,
          agentStatus: null,
          agentProgress: null,
          agentActions: [],
        }),
        { syncCache: true }
      );
      clearPendingPromptSubmission(promptSubmissionId);
      void Promise.allSettled([
        queryClient.invalidateQueries(['agentSession', session.id]),
        queryClient.invalidateQueries(agentSessionsQueryKey),
      ]);
    } catch (error: unknown) {
      if (error instanceof DOMException && error.name === 'AbortError') {
        if (targetSessionId) {
          // Prevent the sync effect from re-hydrating streaming state after the
          // abort controller is cleaned up in the finally block.
          locallyDismissedSessionsRef.current.add(targetSessionId);

          updateSessionViewState(
            targetSessionId,
            (currentState) => {
              const assistantIndex = findIndex(currentState.messages, (message) => message.id === assistantMessageId);
              if (assistantIndex < 0) {
                return {
                  ...currentState,
                  isStreaming: false,
                  agentStatus: null,
                  agentProgress: null,
                  agentActions: [],
                };
              }

              const userIndex = assistantIndex - 1;
              const shouldRemovePairedUser =
                userIndex >= 0 &&
                currentState.messages[userIndex]?.id === userMessageId &&
                currentState.messages[userIndex]?.role === 'user';
              const nextMessages = shouldRemovePairedUser
                ? [...slice(currentState.messages, 0, userIndex), ...slice(currentState.messages, assistantIndex + 1)]
                : [
                    ...slice(currentState.messages, 0, assistantIndex),
                    ...slice(currentState.messages, assistantIndex + 1),
                  ];

              return {
                ...currentState,
                messages: nextMessages,
                isStreaming: false,
                agentStatus: null,
                agentProgress: null,
                agentActions: [],
              };
            },
            { syncCache: true }
          );
          delete optimisticMessageCountsRef.current[targetSessionId];

          // Tell the server to mark the streaming turn as cancelled so re-fetches
          // no longer show a streaming assistant message.
          stopAgentSession(targetSessionId)
            .then((stoppedSession) => {
              queryClient.setQueryData(['agentSession', targetSessionId], stoppedSession);
            })
            .catch(() => {
              // Best-effort — the local UI is already cleared above.
            });

          if (restorePromptOnAbortRef.current[targetSessionId] && activeSessionIdRef.current === targetSessionId) {
            setPromptDraft(text);
          }
        }

        return;
      }

      if (!targetSessionId) {
        return;
      }

      const errorMessage = error instanceof Error ? error.message : 'Failed to get response';

      updateSessionViewState(
        targetSessionId,
        (currentState) => ({
          ...currentState,
          messages: map(currentState.messages, (message) =>
            message.id === assistantMessageId
              ? { ...message, content: errorMessage || 'An error occurred.', isStreaming: false, isError: true }
              : message
          ),
          isStreaming: false,
          hasUnreadUpdate: currentState.hasUnreadUpdate || activeSessionIdRef.current !== targetSessionId,
          agentStatus: null,
          agentProgress: null,
          agentActions: [],
        }),
        { syncCache: true }
      );
    } finally {
      clearPendingPromptSubmission(promptSubmissionId);

      if (targetSessionId) {
        delete restorePromptOnAbortRef.current[targetSessionId];
        delete abortControllersRef.current[targetSessionId];
      }
    }
  };

  React.useEffect(() => {
    // Indirection avoids stale closure when autosubmit resolves after async state updates.
    handleSendPromptRef.current = handleSendPrompt;
  }, [handleSendPrompt]);

  React.useEffect(() => {
    // Autosubmit only fires when route-provided prompt is still the current draft.
    const pendingResolution = resolvePendingAutoSubmit({
      pendingPrompt: pendingAutoSubmitPromptRef.current,
      promptDraft,
      isStreaming: selectedSessionViewState.isStreaming,
    });

    pendingAutoSubmitPromptRef.current = pendingResolution.nextPendingPrompt;

    if (pendingResolution.shouldAutoSubmit) {
      void handleSendPromptRef.current();
    }
  }, [promptDraft, selectedSessionViewState.isStreaming]);

  const handleRetry = (userMessage: string) => {
    // Remove last failed assistant turn (and paired user turn when present) before retry.
    updateSessionViewState(
      activeSessionId,
      (currentState) => {
        const nextMessagesSource = size(currentState.messages) > 0 ? currentState.messages : selectedSessionMessages;
        const lastErrorIndex = findLastIndex(nextMessagesSource, (message) => message.isError);
        if (lastErrorIndex < 0) {
          return currentState;
        }

        const userIndex = lastErrorIndex - 1;
        const nextMessages =
          userIndex >= 0 && nextMessagesSource[userIndex]?.role === 'user'
            ? [...slice(nextMessagesSource, 0, userIndex), ...slice(nextMessagesSource, lastErrorIndex + 1)]
            : [...slice(nextMessagesSource, 0, lastErrorIndex), ...slice(nextMessagesSource, lastErrorIndex + 1)];

        return {
          ...currentState,
          messages: nextMessages,
          hasUnreadUpdate: false,
        };
      },
      { syncCache: true }
    );
    setPromptDraft(userMessage);
    pendingAutoSubmitPromptRef.current = userMessage.trim() || null;
  };

  const handleUsePromptSuggestion = React.useCallback(
    (suggestedPrompt: string) => {
      const text = suggestedPrompt.trim();
      if (!text) {
        return;
      }

      pendingAutoSubmitPromptRef.current = null;
      setPromptDraft(text);
    },
    [setPromptDraft]
  );

  const handleSendPromptSuggestion = React.useCallback(
    (suggestedPrompt: string) => {
      const text = suggestedPrompt.trim();
      if (!text) {
        return;
      }

      if (promptDraft.trim() === text && !selectedSessionViewState.isStreaming) {
        pendingAutoSubmitPromptRef.current = null;
        void handleSendPromptRef.current();
        return;
      }

      setPromptDraft(text);
      pendingAutoSubmitPromptRef.current = text;
    },
    [promptDraft, selectedSessionViewState.isStreaming, setPromptDraft]
  );

  const resolveWorkspaceNavigationSession = React.useCallback(async (): Promise<AgentSession | null> => {
    const pendingSessionId = pendingPromptSubmissionRef.current?.sessionId;

    if (pendingSessionId) {
      return (
        queryClient.getQueryData<AgentSession>(['agentSession', pendingSessionId]) ||
        (activeSession?.id === pendingSessionId ? activeSession : null) ||
        find(knownSessions, (session) => session.id === pendingSessionId) ||
        null
      );
    }

    if (ensureSessionPromiseRef.current) {
      return ensureSessionPromiseRef.current;
    }

    return activeSession || null;
  }, [activeSession, knownSessions, queryClient]);

  const navigateToReportCanvas = (initialUserPrompt?: string, session?: AgentSession | null) => {
    const resolvedSession = session || activeSession || null;
    const sessionLabId = resolvedSession?.sessionData.reportState.filters?.labId;

    openStudyReportRoute({
      initialUserPrompt,
      keepDrawerOpen: true,
      sessionId: resolvedSession?.id || activeSessionId || undefined,
      studyIds: resolvedSession?.studyIds || selectedStudyIds,
      activeStudyId:
        resolvedSession?.sessionData.reportState.activeStudyId ||
        first(resolvedSession?.studyIds) ||
        selectedStudyId ||
        null,
      labId: sessionLabId || labId,
    });
  };

  const handleOpenWorkspace = React.useCallback(async () => {
    const hasPendingPromptSubmission = Boolean(pendingPromptSubmissionRef.current);
    const session = await resolveWorkspaceNavigationSession();

    navigateToReportCanvas(hasPendingPromptSubmission ? undefined : promptDraft.trim() || undefined, session);
  }, [promptDraft, resolveWorkspaceNavigationSession]);

  const handleStartNewChat = () => {
    if (selectedSessionViewState.isStreaming) {
      stopStreamingSession(activeSessionId, { restorePrompt: false });
    }
    pendingAutoSubmitPromptRef.current = null;
    setPromptDraft('');
    setFallbackSessionId(null);

    const currentContextStudyIds = size(selectedStudyIds) > 0 ? selectedStudyIds : activeSession?.studyIds || [];
    const currentContextActiveStudyId = selectedStudyId || first(currentContextStudyIds) || null;

    openStudyReportRoute({
      keepDrawerOpen: true,
      studyIds: currentContextStudyIds,
      activeStudyId: currentContextActiveStudyId,
      labId,
    });
  };

  const handleStop = React.useCallback(() => {
    stopStreamingSession(activeSessionId, { restorePrompt: true });
  }, [activeSessionId, stopStreamingSession]);

  return {
    messages: selectedSessionViewState.messages,
    promptSendState,
    isStreaming: selectedSessionViewState.isStreaming,
    agentStatus: selectedSessionViewState.agentStatus,
    agentProgress: selectedSessionViewState.agentProgress,
    agentActions: selectedSessionViewState.agentActions,
    tokenTraces: selectedSessionViewState.tokenTraces,
    sessionActivityById,
    backgroundSessionActivityCount,
    hiddenSessionActivityCount,
    isChatLoading,
    clearSessionViewState,
    stopStreamingSession,
    stopAllStreamingSessions,
    handleSendPrompt,
    handleRetry,
    handleUsePromptSuggestion,
    handleSendPromptSuggestion,
    handleOpenWorkspace,
    handleStartNewChat,
    handleStop,
  };
};
